import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Megaphone, Sparkles, Send, Check } from "lucide-react";

const TITLE_TEXT = "🚀 New Feature: Call Recording Export";
const MESSAGE_TEXT = "You can now export all your call recordings with one click. Find it in Settings → Export...";
const CYCLE_DURATION = 8000; // 8 seconds total

interface AnnouncementsSectionProps {
  preloaderComplete?: boolean;
}

const AnnouncementsSection = ({ preloaderComplete = true }: AnnouncementsSectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const shouldAnimate = isInView && preloaderComplete;
  
  const [animationPhase, setAnimationPhase] = useState<'idle' | 'typing-title' | 'typing-message' | 'hovering' | 'pressing' | 'success' | 'updating'>('idle');
  const [titleText, setTitleText] = useState("");
  const [messageText, setMessageText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [titleFocused, setTitleFocused] = useState(false);
  const [messageFocused, setMessageFocused] = useState(false);

  useEffect(() => {
    if (!isInView || !preloaderComplete) return;

    const runAnimation = () => {
      // Reset state
      setTitleText("");
      setMessageText("");
      setShowPreview(false);
      setAnimationPhase('idle');
      setTitleFocused(false);
      setMessageFocused(false);

      // Phase 1: Focus title and start typing (delay 500ms)
      setTimeout(() => {
        setTitleFocused(true);
        setAnimationPhase('typing-title');
      }, 500);

      // Phase 2: Type title character by character
      let titleIndex = 0;
      const titleInterval = setInterval(() => {
        if (titleIndex < TITLE_TEXT.length) {
          setTitleText(TITLE_TEXT.slice(0, titleIndex + 1));
          titleIndex++;
        } else {
          clearInterval(titleInterval);
        }
      }, 45);

      // Phase 3: Focus message and start typing (after title done ~1.8s)
      setTimeout(() => {
        setTitleFocused(false);
        setMessageFocused(true);
        setAnimationPhase('typing-message');
      }, 2200);

      // Phase 4: Type message character by character
      setTimeout(() => {
        let messageIndex = 0;
        const messageInterval = setInterval(() => {
          if (messageIndex < MESSAGE_TEXT.length) {
            setMessageText(MESSAGE_TEXT.slice(0, messageIndex + 1));
            messageIndex++;
          } else {
            clearInterval(messageInterval);
          }
        }, 35);
      }, 2200);

      // Phase 5: Hover button (after message done ~5.5s)
      setTimeout(() => {
        setMessageFocused(false);
        setAnimationPhase('hovering');
      }, 5500);

      // Phase 6: Press button
      setTimeout(() => {
        setAnimationPhase('pressing');
      }, 5800);

      // Phase 7: Success state
      setTimeout(() => {
        setAnimationPhase('success');
      }, 6000);

      // Phase 8: Update preview
      setTimeout(() => {
        setAnimationPhase('updating');
        setShowPreview(true);
      }, 6200);

      // Phase 9: Reset to idle before loop
      setTimeout(() => {
        setAnimationPhase('idle');
      }, 7500);
    };

    // Start animation
    runAnimation();

    // Loop the animation
    const interval = setInterval(runAnimation, CYCLE_DURATION);

    return () => clearInterval(interval);
  }, [isInView]);

  return (
    <section ref={ref} className="relative py-16 md:py-32 px-4 md:px-6 overflow-hidden">
      {/* Moderate green gradient background */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.10) 0%, transparent 60%)' }} />
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left: Visual */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={shouldAnimate ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="order-2 lg:order-1 relative"
          >
            <div className="bg-card/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-emerald-500/8 dark:border-emerald-400/12 shadow-xl">
              {/* Announcement Creation */}
              <div className="mb-4 md:mb-6">
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="font-semibold text-sm md:text-base text-foreground">New Announcement</h3>
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <div className="text-[10px] md:text-xs text-muted-foreground mb-1">Title</div>
                    <div 
                      className={`h-9 md:h-10 bg-secondary rounded-lg border px-2 md:px-3 flex items-center text-xs md:text-sm text-foreground transition-all duration-200 ${
                        titleFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                      }`}
                    >
                      {titleText}
                      {titleFocused && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                          className="inline-block w-0.5 h-3 md:h-4 bg-foreground ml-0.5"
                        />
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <div className="text-[10px] md:text-xs text-muted-foreground mb-1">Message</div>
                    <div 
                      className={`h-20 md:h-24 bg-secondary rounded-lg border p-2 md:p-3 text-xs md:text-sm text-muted-foreground transition-all duration-200 ${
                        messageFocused ? 'border-primary ring-2 ring-primary/20' : 'border-border'
                      }`}
                    >
                      {messageText}
                      {messageFocused && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                          className="inline-block w-0.5 h-3 bg-muted-foreground ml-0.5"
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                      <span className="text-[10px] md:text-xs text-muted-foreground">All clients (156)</span>
                    </div>
                    <motion.div
                      animate={{
                        scale: animationPhase === 'hovering' ? 1.02 : animationPhase === 'pressing' ? 0.97 : 1,
                        boxShadow: animationPhase === 'hovering' ? '0 4px 12px rgba(0,0,0,0.15)' : '0 0 0 rgba(0,0,0,0)'
                      }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      className={`flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium cursor-pointer transition-colors duration-200 ${
                        animationPhase === 'success' 
                          ? 'bg-green-600 text-white' 
                          : 'bg-primary text-primary-foreground'
                      }`}
                    >
                      <AnimatePresence mode="wait">
                        {animationPhase === 'success' ? (
                          <motion.div
                            key="success"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.15 }}
                            className="flex items-center gap-2"
                          >
                            <Check className="h-4 w-4" />
                            Published
                          </motion.div>
                        ) : (
                          <motion.div
                            key="publish"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.15 }}
                            className="flex items-center gap-2"
                          >
                            <Send className="h-4 w-4" />
                            Publish
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="border-t border-border pt-4 md:pt-6">
                <div className="text-[10px] md:text-xs text-muted-foreground mb-2 md:mb-3">Client Preview</div>
                <AnimatePresence mode="wait">
                  {showPreview ? (
                    <motion.div
                      key="preview-content"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      className="bg-primary/5 rounded-lg md:rounded-xl p-3 md:p-4 border border-primary/20"
                    >
                      <div className="flex items-start gap-2 md:gap-3">
                        <motion.div 
                          initial={{ scale: 0.96 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.2, ease: "easeOut" }}
                          className="w-7 md:w-8 h-7 md:h-8 rounded-lg bg-primary flex items-center justify-center shrink-0"
                        >
                          <Megaphone className="h-3 md:h-4 w-3 md:w-4 text-primary-foreground" />
                        </motion.div>
                        <div>
                          <div className="text-xs md:text-sm font-medium text-foreground mb-1">
                            🚀 New Feature: Call Recording Export
                          </div>
                          <p className="text-[10px] md:text-xs text-muted-foreground">
                            You can now export all your call recordings with one click...
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="preview-placeholder"
                      className="bg-primary/5 rounded-lg md:rounded-xl p-3 md:p-4 border border-primary/20 opacity-40"
                    >
                      <div className="flex items-start gap-2 md:gap-3">
                        <div className="w-7 md:w-8 h-7 md:h-8 rounded-lg bg-primary/50 flex items-center justify-center shrink-0">
                          <Megaphone className="h-3 md:h-4 w-3 md:w-4 text-primary-foreground/50" />
                        </div>
                        <div>
                          <div className="text-xs md:text-sm font-medium text-foreground/50 mb-1">
                            Your announcement will appear here
                          </div>
                          <p className="text-[10px] md:text-xs text-muted-foreground/50">
                            Preview how clients will see your message...
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* Right: Text */}
          <div className="order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-2 mb-3 md:mb-4"
            >
              <Megaphone className="h-4 w-4 text-primary" />
              <span className="text-xs md:text-sm text-muted-foreground uppercase tracking-wider font-medium">
                Make It Yours
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground leading-tight mb-4 md:mb-6"
            >
              Make it feel like your <span className="ayra-accent">software</span>.
              <br />
              Because it is.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed"
            >
              Push updates, release notes, and announcements to your clients. Add your personal touch. Keep them engaged. Show them you're always improving.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnnouncementsSection;
